import {
  ALL_FACILITY_STAFF_TIERS,
  ERROR_CODE,
  FACILITY_STAFF_TIER,
  FacilityStaffRegistrationFormInput,
  FacilityStaffRegistrationFormSchema,
  UniversalError,
  isEnabled,
  isFacilityKey,
  isOrganizationKey,
  isUserKey,
  parse,
  parseFacilityStaffTier,
} from "@packages/core";
import { useForm, useStore } from "@tanstack/react-form";
import { Link, createFileRoute, useBlocker, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import {
  DropDownList,
  FacilityStaffRoleSelector,
  FormField,
  FormFieldInput,
  FormFieldInputStyle,
  PageName,
  PaperStyle,
  PasswordInput,
  TypographyStyle,
  buttonStyles,
  showError,
} from "#components";
import {
  $alert,
  $confirm,
  registerFacilityStaff,
  useCurrentUsername,
  useFacilityQuery,
  useFacilityStaff,
  useGetErrorMessage,
  useOperator,
  useTranslate,
} from "#services";
import { css } from "#styled-system/css";

export const Route = createFileRoute("/_authenticated/facilities/$facilityKey/staffs/registration")(
  { component: Component },
);

function Component() {
  const { facilityKey } = Route.useParams();

  const { data: facility } = useFacilityQuery(facilityKey);
  const getErrorMessage = useGetErrorMessage();
  const navigate = useNavigate();
  const translate = useTranslate();
  const username = useCurrentUsername();

  const $operator = useOperator(isUserKey(username) && username);
  const $facilityStaff = useFacilityStaff(facilityKey, isUserKey(username) && username);

  const schema = FacilityStaffRegistrationFormSchema();
  const defaultValues = useMemo(function (): FacilityStaffRegistrationFormInput {
    return {
      emailAddress: "",
      name: "",
      password: "",
      roles: [],
      tier: FACILITY_STAFF_TIER.DEFAULT,
      userKey: "",
    };
  }, []);

  const { Field, handleSubmit, ...form } = useForm({
    canSubmitWhenInvalid: true,
    defaultValues,
    async onSubmit({ value: input }) {
      try {
        const output = parse(schema, input);
        console.info("[onSubmit]", new Date(), { input, output });
        const { facilityStaff } = await registerFacilityStaff({
          emailAddress: output.emailAddress,
          facilityKey,
          name: output.name,
          password: output.password,
          roles: output.roles,
          tier: output.tier,
          userKey: output.userKey ? output.userKey : undefined,
        });

        await $alert({
          color: "success",
          content: translate("%Register.3%", { $: { name: facilityStaff.name } }),
          title: translate("%Register.0%"),
        });

        await navigate({
          ignoreBlocker: true,
          params: { facilityKey },
          to: "/facilities/$facilityKey/staffs",
        });
      } catch (error) {
        showError(error);
      }
    },
    onSubmitInvalid({ value: input }) {
      console.info("[onSubmitInvalid]", new Date(), { input });
      showError(new UniversalError(ERROR_CODE.INVALID_FORM));
    },
    validators: { onSubmit: schema },
  });

  const submitting = useStore(form.store, (state) => state.isSubmitting);

  useBlocker({
    async shouldBlockFn() {
      return (
        (!form.store.state.isDefaultValue || form.store.state.isSubmitting) &&
        !(await $confirm({
          color: "warning",
          content: translate("%Form.1%"),
          no: translate("%Stay.0%"),
          title: translate("%Form.0%"),
          yes: translate("%Leave.0%"),
        }))
      );
    },
  });

  return (
    <main>
      <PageName>
        {facility
          ? translate("%StaffRegistration.0%") + " - " + facility.name
          : translate("%Loading.0%")}
      </PageName>

      <div
        className={css({
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          paddingX: "24px",
          paddingY: "16px",
          width: "100%",
        })}
      >
        <form
          className={css(PaperStyle.raw({ borderRadius: "12px" }), {
            maxWidth: "380px",
            paddingX: "24px",
            paddingY: "16px",
            width: "stretch",
          })}
          onSubmit={function (event) {
            event.preventDefault();
            handleSubmit();
          }}
        >
          <h1 className={TypographyStyle({ lineHeight: 1 })}>
            <span className={TypographyStyle({ fontSize: "18px", fontWeight: 600 })}>
              {translate("%StaffRegistration.0%")}
            </span>
            <span className={TypographyStyle({ color: "primary/60", fontSize: "14px" })}>
              {facility ? " " + facility.name : ""}
            </span>
          </h1>

          <Field name="userKey" validators={{ onChange: schema.entries.userKey }}>
            {function (field) {
              return (
                <FormField
                  caution={field.state.meta.errors
                    .map(function (error) {
                      return getErrorMessage(error);
                    })
                    .join("")}
                  className={css({ marginTop: "16px", width: "100%" })}
                  description={translate("%UserKey.1%")}
                  invalid={field.state.meta.errors.length > 0}
                  label={translate("%UserKey.0%")}
                >
                  <FormFieldInput
                    className={FormFieldInputStyle({ width: "50%" })}
                    id={field.name}
                    onBlur={function () {
                      field.handleBlur();
                    }}
                    onChange={function (event) {
                      field.handleChange(event.target.value as any);
                    }}
                    value={field.state.value}
                  />
                </FormField>
              );
            }}
          </Field>

          <Field name="name" validators={{ onChange: schema.entries.name }}>
            {function (field) {
              return (
                <FormField
                  caution={field.state.meta.errors
                    .map(function (error) {
                      return getErrorMessage(error);
                    })
                    .join("")}
                  className={css({ marginTop: "16px", width: "100%" })}
                  invalid={field.state.meta.errors.length > 0}
                  label={translate("%Name.0%")}
                  required
                >
                  <FormFieldInput
                    className={FormFieldInputStyle({ width: "50%" })}
                    id={field.name}
                    onBlur={() => field.handleBlur()}
                    onChange={function (event) {
                      field.handleChange(event.target.value);
                    }}
                    value={field.state.value}
                  />
                </FormField>
              );
            }}
          </Field>

          <Field name="emailAddress" validators={{ onChange: schema.entries.emailAddress }}>
            {function (field) {
              return (
                <FormField
                  caution={field.state.meta.errors
                    .map(function (error) {
                      return getErrorMessage(error);
                    })
                    .join("")}
                  className={css({ marginTop: "16px" })}
                  invalid={field.state.meta.errors.length > 0}
                  label={translate("%EmailAddress.0%")}
                  required
                  title=""
                >
                  <FormFieldInput
                    className={css(FormFieldInputStyle.raw({}), { width: "100%" })}
                    id={field.name}
                    onBlur={function () {
                      field.handleBlur();
                    }}
                    onChange={function (event) {
                      field.handleChange(event.target.value);
                    }}
                    type="email"
                    value={field.state.value}
                  />
                </FormField>
              );
            }}
          </Field>

          <Field name="password" validators={{ onChange: schema.entries.password }}>
            {function (field) {
              return (
                <FormField
                  caution={field.state.meta.errors
                    .map(function (error) {
                      return getErrorMessage(error);
                    })
                    .join("")}
                  className={css({ marginTop: "16px" })}
                  id={field.name}
                  invalid={field.state.meta.errors.length > 0}
                  label={translate("%Password.0%")}
                  required
                >
                  <PasswordInput
                    className={css({ width: "100%" })}
                    id={field.name}
                    onBlur={function () {
                      field.handleBlur();
                    }}
                    onChange={function (event) {
                      field.handleChange(event.target.value);
                    }}
                    value={field.state.value}
                  />
                </FormField>
              );
            }}
          </Field>

          <Field name="tier" validators={{ onChange: schema.entries.tier }}>
            {function (field) {
              return (
                <FormField
                  caution={getErrorMessage(field.state.meta.errors.at(0))}
                  className={css({ marginTop: "16px", maxWidth: "320px" })}
                  invalid={!field.state.meta.isValid}
                  label={translate("%FacilityStaffTier.0%")}
                >
                  <DropDownList
                    className={css({ width: "100%" })}
                    id={field.name}
                    onBlur={function () {
                      field.handleBlur();
                    }}
                    onChange={function (event) {
                      field.handleChange(
                        parseFacilityStaffTier(Number.parseInt(event.target.value)),
                      );
                    }}
                    required
                    value={field.state.value}
                  >
                    {ALL_FACILITY_STAFF_TIERS.filter(function (facilityStaffTier) {
                      return (
                        isEnabled($operator) ||
                        isOrganizationKey(username) ||
                        isFacilityKey(username) ||
                        (isEnabled($facilityStaff) && facilityStaffTier >= $facilityStaff.tier)
                      );
                    }).map(function (tier) {
                      return (
                        <option key={tier} value={tier}>
                          {translate("%FacilityStaffTier." + tier + ".0%")}
                        </option>
                      );
                    })}
                  </DropDownList>
                </FormField>
              );
            }}
          </Field>

          <Field name="roles" validators={{ onChange: schema.entries.roles }}>
            {function (field) {
              return (
                <FormField
                  caution={getErrorMessage(field.state.meta.errors.at(0))}
                  className={css({ marginTop: "16px", maxWidth: "320px" })}
                  invalid={!field.state.meta.isValid}
                  label={translate("%FacilityStaffRole.0%")}
                >
                  <FacilityStaffRoleSelector
                    onChange={function (_, values) {
                      field.handleChange(values);
                    }}
                    values={field.state.value}
                  />
                </FormField>
              );
            }}
          </Field>

          <div
            className={css({
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "8px",
              marginTop: "16px",
            })}
          >
            <Link
              className={css(
                TypographyStyle.raw({ ellipsis: true }),
                buttonStyles.raw({ variant: "outline" }),
                { width: "100%" },
              )}
              params={{ facilityKey }}
              to="/facilities/$facilityKey/staffs"
            >
              {translate("%Cancel.0%")}
            </Link>

            <button
              className={css(
                TypographyStyle.raw({ ellipsis: true }),
                buttonStyles.raw({ color: "primary" }),
                { width: "100%" },
              )}
              disabled={submitting}
              type="submit"
            >
              {translate("%Save.0%")}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}
